import styled from '@emotion/styled';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { buyerSeatService } from '../../api/services/buyer-seat.service';
import { Field, Form, Formik, FormikErrors } from 'formik';
import { useAlert } from '../shared/AlertProvider';
import { CURRENCY, TYPE } from '../../api/services/dto/buyer-seat-dto';
import MenuItem from '@mui/material/MenuItem';
import { BuyerSeat } from '../../util/admin-util';
import { bidder_names, bidder_id } from '../../util/buyer-seats';
import { BuyerSeatRequestDto } from '../../api/services/dto/buyer-seat-request-dto';
import { errorBuyerSeatHandling } from '../../util/error-hadling-util';
import SelectOrganization from '../organization/create/assign-user-to-organization/cards/SelectOrganization';
import { useNavigate } from 'react-router-dom';
import { getColor } from '../../theme/colors/colors';
import BuyerSeatBreadcrumb from './BuyerSeatBreadcrumb';


export const validate = (values: BuyerSeat): FormikErrors<BuyerSeat> => {
  const errors: FormikErrors<BuyerSeat> = {};
  console.log(`Validate: ${JSON.stringify(values)}`);
  if (!values.type) {
    errors.type = 'Type is required';
  }
  if (!values.ssp) {
    errors.ssp = 'SSP is required';
  }
  if (!values.name) {
    errors.name = 'Buyer name is required';
  }
  if (!values.dsp) {
    errors.dsp = 'DSP (bidder) name is required';
  }
  if (!values.dspId) {
    errors.dspId = 'DSP (bidder) ID is required';
  }
  if (!values.buyerSeatId) {
    errors.buyerSeatId = 'Buyer ID (code/id) is required';
  }
  if (values.type === TYPE.MEMBER && isNaN(Number(values.buyerSeatId))) {
    errors.buyerSeatId = 'Buyer ID of Buyer Member is required to be a number';
  }
  if (!values.currency) {
    errors.currency = 'Currency is required';
  }
  if (!values.organization?.organizationId) {
    errors.organization = { organizationId: 'Organization is required', name: '' };
  }
  return errors;
};

const BuyerSeatForm = () => {
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const [buyerSeat, setBuyerSeat] = useState<BuyerSeat>({
    ssp: 'Xandr',
    dsp: '',
    dspId: 0,
    buyerSeatId: '',
    name: '',
    organization: { organizationId: '', name: '' },
    currency: CURRENCY.CHF,
    type: TYPE.SEAT,
  });

  const [_, setDirtyState] = useState<boolean>(false);

  useEffect(() => {
    console.log(buyerSeat);
  }, [buyerSeat]);

  const createBuyerSeat = async (values: BuyerSeat) => {
    const request: BuyerSeatRequestDto = {
      ssp: values.ssp,
      dsp: values.dsp,
      dspId: values.dspId,
      buyerSeatId: values.buyerSeatId,
      name: values.name,
      tenantId: values.organization.organizationId,
      currency: values.currency,
      type: values.type,
    };

    await buyerSeatService
      .createBuyerSeat(request)
      .then(() => {
        setAlert({ open: true, title: 'Created a buyer seat', severity: 'success' });
        navigate('/buyer-seat/list');
      })
      .catch((reason) => {
        const formedErrorReponse = errorBuyerSeatHandling(reason);
        console.error(`Failed to create buyer seat: ${reason}`);
        setAlert({ open: true, title: formedErrorReponse.title, severity: 'error' });
      });
    setBuyerSeat(values);
  };

  return (
    <PageWrapper>
      <BuyerSeatBreadcrumb
        items={[
          { label: 'Buyer Seats', to: '/buyer-seat/list' },
          { label: 'Create New Buyer Seat' },
        ]}
      />

      <Formik
        initialValues={buyerSeat}
        onSubmit={createBuyerSeat}
        validate={validate}
        validateOnBlur={true}
        validateOnMount={true}
      >
        {({ errors, touched, isValid, setFieldValue }) => (
          <Form
            id="create-buyer-seat-form"
            onChange={() => {
              setDirtyState(true);
            }}
          >
            <SubHeader>
              <HeaderLeft>
                <CardTitle>Create a Buyer for Tenant (Organization)</CardTitle>
                <CardSubtitle>Fill in the details to create a new buyer seat for your organization</CardSubtitle>
              </HeaderLeft>
              <HeaderRight>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/buyer-seat/list')}
                  sx={{
                    backgroundColor: '#FFFFFF',
                    color: 'text.primary',
                    borderColor: '#0000001A',
                    '&:hover': { backgroundColor: '#F5F5F5', borderColor: '#0000003A' },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => {
                    if (!isValid) {
                      setAlert({
                        open: true,
                        severity: 'error',
                        title: 'Please complete the form',
                      });
                    }
                  }}
                >
                  Create
                </Button>
              </HeaderRight>
            </SubHeader>

            <Card>
              <FormGrid>
                {/* Row 1: SSP + Buyer Type */}
                <Field name="ssp" id="ssp" type="text">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      label="SSP"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && !!meta.error ? 'SSP is required' : ' '}
                      id="ssp-select"
                      select
                      variant="outlined"
                      fullWidth
                    >
                      {['Xandr', 'Magnite', 'PubMatic', 'Google Ad Manager'].map((ssp) => (
                        <MenuItem key={ssp} value={ssp}>
                          {ssp}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Field>
                <Field name="type" id="type" type="text">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      label="Buyer Type"
                      error={!!meta.touched && !!meta.error}
                      helperText={!!meta.touched && !!meta.error ? 'Buyer Type is required' : ' '}
                      id="type-select"
                      select
                      variant="outlined"
                      fullWidth
                    >
                      <MenuItem key={TYPE.SEAT} value={TYPE.SEAT}>
                        {TYPE.SEAT}
                      </MenuItem>
                      <MenuItem key={TYPE.MEMBER} value={TYPE.MEMBER}>
                        {TYPE.MEMBER}
                      </MenuItem>
                    </TextField>
                  )}
                </Field>

                {/* Row 2: DSP Name + DSP ID */}
                <Field name="dsp" id="dsp" type="text">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      label="DSP Name (Bidder Name)"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && !!meta.error ? 'DSP (bidder_name) is required' : ' '}
                      id="dsp-select"
                      select
                      variant="outlined"
                      fullWidth
                      onChange={(e) => {
                        field.onChange(e);
                        setFieldValue('dspId', bidder_id[e.target.value as keyof typeof bidder_id] ?? '');
                      }}
                    >
                      {bidder_names.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Field>
                <Field name="dspId" id="dspId" type="text">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      label="DSP ID (Bidder ID)"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && !!meta.error ? 'DSP ID (bidder_id) is required' : ' '}
                      fullWidth
                    />
                  )}
                </Field>

                {/* Row 3: Seat/Member ID + Seat/Member Name */}
                <Field name="buyerSeatId" id="buyerSeatId" type="text">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      label="Seat / Member ID (Code)"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && !!meta.error ? errors.buyerSeatId : ' '}
                      fullWidth
                    />
                  )}
                </Field>
                <Field name="name" id="name" type="text">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      label="Seat / Member Name"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && !!meta.error ? 'Buyer Name (name) is required' : ' '}
                      fullWidth
                    />
                  )}
                </Field>

                {/* Row 4: Currency + Organization */}
                <Field name="currency" id="currency" type="text">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      label="Currency"
                      error={!!meta.touched && !!meta.error}
                      helperText={!!meta.touched && !!meta.error ? 'Currency is required' : ' '}
                      id="currency-select"
                      select
                      variant="outlined"
                      fullWidth
                    >
                      <MenuItem key={CURRENCY.CHF} value={CURRENCY.CHF}>
                        {CURRENCY.CHF}
                      </MenuItem>
                      <MenuItem key={CURRENCY.EUR} value={CURRENCY.EUR}>
                        {CURRENCY.EUR}
                      </MenuItem>
                    </TextField>
                  )}
                </Field>
                <OrgFieldWrapper>
                  <SelectOrganization
                    selected={buyerSeat.organization}
                    name="organization"
                    id="organization"
                    setCustomDirtyState={() => setDirtyState(true)}
                    helperText={!!(errors?.organization?.organizationId && touched?.organization)}
                  />
                </OrgFieldWrapper>
              </FormGrid>
            </Card>
          </Form>
        )}
      </Formik>
    </PageWrapper>
  );
};

export default BuyerSeatForm;

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  height: 100%;
`;

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h4`
  color: ${getColor('text')};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.31px;
  margin: 0;
`;

const CardSubtitle = styled.p`
  color: ${getColor('text')};
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  opacity: 0.6;
  margin: 4px 0 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Card = styled.div`
  background-color: #ffffff;
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
    0px 1px 10px 0px rgba(0, 0, 0, 0.12);
  margin-top: 24px;
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;
`;

const OrgFieldWrapper = styled.div`
  width: 100%;

  .MuiAutocomplete-root,
  .MuiFormControl-root {
    width: 100%;
  }

  /* Match the helper text spacing of other fields */
  .MuiFormHelperText-root {
    min-height: 20px;
  }
`;
